import { HttpStatusCode } from 'axios';
import { t } from 'i18next';
import moment from 'moment';
import { MarkAsFreeApi } from '../../../../utils/Api/Booking';
import { api } from '../../../../utils/Api/POS/index';
import { createZeroAmountBookingPayment } from '../../../../scenes/POS/UI/Sales-Page/UI/Create/Modals/Payment/utils/payment-helpers';
import { clearCompletedPayments } from '../../../../scenes/POS/UI/Sales-Page/UI/Create/Modals/Payment/utils/payment-protection';
import { buildMarkAsFreePosDiscountFields, buildPosDiscountFields, getBookingDiscounts, getBookingLineSubTotal, isBookingMarkAsFreeService } from './bookingDiscounts';

export class BookingDetailsApi {
    static api = null;
    static apiFetcher = null;
    static bookingId = null;
    static toast = null;
    static bookingDetails = null;
    static salesDetails = null;
    static navigate = null;
    static closeForm = null;
    static saleResponseId = null;
    static noShowSettings = null;
    static settings = null;
    static refetchSalesDetails = () => {};
    static taxList = [];
    constructor({
        apiFetcher,
        bookingId,
        toast,
        closeForm,
        salesDetails,
        navigate,
        bookingDetails,
        noShowSettings,
        settings,
        refetchSalesDetails,
        taxList,
    }) {
        this.apiFetcher = apiFetcher;
        this.bookingId = bookingId;
        this.toast = toast;
        this.closeForm = closeForm;
        this.salesDetails = salesDetails;
        this.navigate = navigate;
        this.bookingDetails = bookingDetails;
        this.saleResponseId = null;
        this.noShowSettings = noShowSettings;
        this.settings = settings;
        this.refetchSalesDetails = refetchSalesDetails;
        this.taxList = taxList;
    }

    // #method 1 : Get Booking Details By ID
    async getDetails({ setBookingDetails, setStatus, setLoading, settings }) {
        try {
            const response = await this?.apiFetcher(
                `api/v1/store/booking/${this.bookingId}?all_in_group_bookings=true`,
            );
            if (response.status === HttpStatusCode.Ok) {
                this.bookingDetails = response?.data?.data;
                setBookingDetails(response?.data?.data);
                if (response?.data?.data?.booking?.custom_status_id) {
                    const matchcolor = settings?.profile.custom_statuses?.filter((val) => {
                        if (response?.data?.data?.booking?.custom_status_id === val.id) {
                            return val.status_color;
                        }
                    });
                    if (matchcolor.length > 0) {
                        setStatus(response?.data?.data?.booking?.custom_status_id);
                    } else {
                        setStatus(response?.data?.data?.booking?.status);
                    }
                } else {
                    setStatus(response?.data?.data?.booking?.status);
                }
            }

            if (response?.data?.data?.booking?.sales_id) {
                this?.refetchSalesDetails(response?.data?.data?.booking?.sales_id);
            }

            return response?.data?.data;
        } catch (error) {
            this?.toast.error(t('History.FetchErr'));
            return null;
        } finally {
            // Callers that chain another request onto the booking omit setLoading and clear it themselves
            setLoading?.(false);
        }
    }

    // #method 2 : Update Booking Status
    async updateBookingStatus({ status, admin_charge_cancellation = false, showToast = true }) {
        if (!this.bookingDetails?.booking?.id) {
            this?.toast.error(t('Calendar.ToastErrIdMissing'));
            return false;
        }

        const payload = { id: this.bookingDetails?.booking?.id, custom_status_id: null };
        let endpoint = '';

        switch (status) {
            case 'COMPLETED':
                endpoint = `api/v1/store/booking/complete`;
                break;
            case 'CANCELLED':
                endpoint = `api/v1/store/booking/cancel`;
                payload.admin_charge_cancellation = admin_charge_cancellation;
                break;
            case 'BOOKED':
                endpoint = `api/v1/store/booking/pending`;
                break;
            case 'NOSHOW':
                endpoint = `api/v1/store/booking/noshow`;
                break;
            case 'DELETED':
                endpoint = `api/v1/store/booking/delete`;
                break;
            case 'RESCHEDULED':
                endpoint = `api/v1/store/booking/reschedule`;
                payload.booking_date = moment(this?.bookingDetails?.booking?.booking_datetime_start).format(
                    'YYYY-MM-DD',
                );
                payload.time_slot = `${moment(this?.bookingDetails?.booking?.booking_datetime_start).format('HH:mm')} - ${moment(this?.bookingDetails?.booking?.booking_datetime_end).format('HH:mm')}`;
                payload.employee_id = this?.bookingDetails?.booking?.employee_id;
                payload.total_amount = this?.bookingDetails?.booking?.booking_details?.price;
                break;
            default:
                endpoint = `/api/v1/store/booking/pending`;
                payload.custom_status_id = status;
            // return false;
        }

        try {
            const response = await this?.apiFetcher.post(endpoint, { ...payload, google_calendar_sync: true });
            if (response.data.success) {
                if (showToast) {
                    this.toast.success(response.data.msg || t('Calendar.ToastSuccessStatus'));
                }
                if (status === 'NOSHOW' && !this?.noShowSettings?.enable) {
                    this?.closeForm();
                    return;
                }
                this?.closeForm();
                return true;
            } else {
                if (showToast) {
                    this.toast.error(response.data.msg || t('Calendar.ToastErrStatus'));
                }
                return false;
            }
        } catch (error) {
            return false;
        }
    }

    // #Method 3 : Handle Email Send , SMS Send By type
    async handleSend({ type, setBookingDetails, setStatus, setLoading, settings }) {
        let param = type === 'SMS' ? 'send-sms' : 'send-email';

        try {
            const response = await this?.apiFetcher.post(`api/v1/store/booking/${param}/${this?.bookingId}`);
            if (response.data.success) {
                this?.toast.success(
                    param === 'send-sms' ? t('Calendar.SendSmsSuccess') : t('Calendar.SendEmailSuccess'),
                );
                this?.getDetails({ setBookingDetails, setStatus, setLoading, settings });
            }
        } catch (error) {
            this?.toast.error(param === 'send-sms' ? t('Calendar.SendSmsError') : t('Calendar.SendEmailError'));
        }
    }

    getBookingServicesMap() {
        const servicesMap = new Map();
        const mainBooking = this?.bookingDetails?.booking;
        const bookingDiscounts = getBookingDiscounts(this?.bookingDetails);
        const isInspectionEnabled = Boolean(this?.settings?.profile?.inspection_module);

        const mapBookingService = (booking, bookingId) => {
            const serviceId = booking?.service_id ?? booking?.booking_details?.service_id;
            if (serviceId == null) return;

            const isMarkAsFree = isBookingMarkAsFreeService(booking, this?.bookingDetails, isInspectionEnabled);
            const discountFields = isMarkAsFree
                ? buildMarkAsFreePosDiscountFields(getBookingLineSubTotal(booking), t('Calendar.MarkAsFree'))
                : buildPosDiscountFields(bookingDiscounts, serviceId);

            servicesMap.set(Number(serviceId), {
                serviceId,
                price:
                    discountFields.originalPrice != null
                        ? discountFields.originalPrice
                        : isMarkAsFree
                          ? getBookingLineSubTotal(booking)
                          : Number(booking?.booking_details?.price) ?? 0,
                serviceName: booking?.booking_details?.service_name || '',
                employeeId: booking?.booking_details?.employee_id || '',
                isSpecial: booking?.service?.is_special || booking?.service?.isSpecial,
                bookingId,
                discountFields,
            });
        };

        if (mainBooking) {
            mapBookingService(mainBooking, this?.bookingId);
        }

        this?.bookingDetails?.group_bookings?.forEach((booking) => {
            mapBookingService(booking, booking?.id);
        });

        return servicesMap;
    }

    buildServiceItemName(bookingService) {
        if (bookingService?.isSpecial) {
            return `${bookingService.serviceName} (${t('SpOffers.SpOffer')})`;
        }
        return bookingService?.serviceName || '';
    }

    getSalesItemIdByServiceId(serviceId) {
        const salesItem = this?.salesDetails?.items?.find(
            (item) => item?.itemType === 'SERVICE' && Number(item?.serviceId) === Number(serviceId),
        );
        return salesItem?.id ?? null;
    }

    /**
     * Discount already stored on a sale line that the booking cannot account for — one added by
     * hand in POS (discountSource null) or recorded as a settlement. Rebuilding the line from the
     * booking alone would drop it, and the following edit-sale would erase it from the sale.
     */
    getExtraSalesLineDiscount(existingSalesItem, bookingShare, maxAmount) {
        const rows = existingSalesItem?.discounts || [];
        const existingTotal = Number(existingSalesItem?.discountAmount) || 0;
        if (existingTotal <= 0) {
            return { amount: 0, discount: null };
        }

        const taggedBookingAmount = rows
            .filter((row) => row?.discountSource === 'BOOKING')
            .reduce((sum, row) => sum + (Number(row?.discountAmount) || 0), 0);

        // Tagged rows state exactly how much came from the booking. Untagged rows predate
        // discountSource, so their total already includes whatever the booking granted.
        const rawAmount =
            taggedBookingAmount > 0
                ? Math.max(0, existingTotal - taggedBookingAmount)
                : Math.max(0, existingTotal - Number(bookingShare || 0));
        const amount = Math.min(rawAmount, Math.max(0, Number(maxAmount) || 0));

        if (amount <= 0) {
            return { amount: 0, discount: null };
        }

        const source = rows.find((row) => row?.discountSource !== 'BOOKING');
        return {
            amount,
            discount: {
                discountId: source?.discountId ?? null,
                couponId: source?.couponId ?? null,
                discountName: source?.discountName || 'Amount Discount',
                discountAmount: amount,
                amountType: 'VARIABLE_AMOUNT',
                amount,
                discountSource: source?.discountSource ?? null,
            },
        };
    }

    buildServiceCartItem({ bookingService, salesDetailId = null, serviceTax, existingSalesItem = null }) {
        const fullPrice = Number(bookingService?.price) ?? 0;
        const discountFields = bookingService?.discountFields || {
            discountAmount: 0,
            discountPercentage: 0,
            discountType: 'VARIABLE_PERCENTAGE',
            discounts: [],
        };
        const bookingShare = Number(discountFields.discountAmount) || 0;
        const hasDiscount = bookingShare > 0;
        const extra = this.getExtraSalesLineDiscount(
            existingSalesItem,
            bookingShare,
            Math.max(0, fullPrice - bookingShare),
        );
        const hasExtra = extra.amount > 0;

        const discountAmount = Math.min(fullPrice, bookingShare + extra.amount);
        const netAmount = hasExtra
            ? Math.max(0, fullPrice - discountAmount)
            : hasDiscount
              ? discountFields.finalAmount != null
                  ? Number(discountFields.finalAmount)
                  : Math.max(0, fullPrice - bookingShare)
              : fullPrice;
        // Staff can discount this line further in POS (e.g. settling the gap a deposit left behind).
        // The cart rebuilds item.discounts on every edit, so the booking's own share has to travel
        // on the line itself to stay attributable afterwards.
        const bookingDiscount = discountFields.discounts?.find(
            (discount) => discount?.discountSource === 'BOOKING',
        );

        const discounts = hasDiscount ? [...(discountFields.discounts || [])] : [];
        if (hasExtra) {
            discounts.push(extra.discount);
        }

        return {
            salesDetailId,
            productId: null,
            serviceId: bookingService?.serviceId,
            itemName: this.buildServiceItemName(bookingService),
            itemType: 'SERVICE',
            saleType: 'SALE',
            quantity: 1,
            amount: netAmount,
            discountAmount,
            discountPercentage: hasExtra ? 0 : hasDiscount ? Number(discountFields.discountPercentage) || 0 : 0,
            taxAmount: 0,
            price: fullPrice,
            note: '',
            employeeId: bookingService?.employeeId || Number(localStorage.getItem('employee_id')),
            description: '',
            discounts,
            discountType: hasExtra ? 'VARIABLE_AMOUNT' : discountFields.discountType || 'VARIABLE_PERCENTAGE',
            bookingDiscountAmount: Number(bookingDiscount?.discountAmount) || 0,
            bookingDiscountName: bookingDiscount?.discountName || null,
            bookingId: bookingService?.bookingId,
            saleId: this?.bookingDetails?.booking?.sales_id || null,
            tax: serviceTax ? [serviceTax] : [],
        };
    }

    getAlreadyPaidAmount() {
        return (
            this?.salesDetails?.transactions
                ?.filter(
                    (transaction) =>
                        transaction?.paymentType !== 'OUTSTANDING' &&
                        !transaction?.paymentType?.toLowerCase()?.includes('credit'),
                )
                ?.reduce((acc, transaction) => acc + (Number(transaction?.amount) || 0), 0) || 0
        );
    }

    getServiceTax() {
        return this?.taxList?.data?.find(
            (tax) =>
                tax?.isActive &&
                tax?.applyTaxTo === 'ALL_ITEMS_SERVICES' &&
                tax?.includeTaxInItemPrice === true &&
                tax?.taxName === 'VAT',
        );
    }

    buildMarkAsFreeSalesPayload() {
        const serviceTax = this.getServiceTax();
        const bookingServicesMap = this.getBookingServicesMap();
        const items = [];

        bookingServicesMap.forEach((bookingService) => {
            items.push(
                this.buildServiceCartItem({
                    bookingService,
                    serviceTax,
                }),
            );
        });

        const subTotal = items.reduce((sum, item) => sum + (Number(item.price) || 0), 0);
        const totalDiscount = items.reduce((sum, item) => sum + (Number(item.discountAmount) || 0), 0);
        const netTotal = items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
        const salesDiscounts = items
            .flatMap((item) => item.discounts || [])
            .map((discount) => ({
                discountId: discount.discountId ?? null,
                couponId: discount.couponId ?? null,
                discountName: discount.discountName ?? t('Calendar.MarkAsFree'),
                discountAmount: Number(discount.discountAmount) || 0,
                amountType: discount.amountType || 'VARIABLE_PERCENTAGE',
                amount: Number(discount.amount) || 0,
            }));

        return {
            data: {
                customerId: this?.bookingDetails?.booking?.outlet_customer?.id || null,
                customerName: this?.bookingDetails?.booking?.outlet_customer?.name || null,
                subTotal,
                totalTax: 0,
                netTotal,
                tenderAmount: 0,
                change: 0,
                roundOff: 0,
                tips: 0,
                salesNote: '',
                salesType: 'SALES',
                sellBy: Number(localStorage.getItem('employee_id')),
                salesDiscounts,
                salesTaxes: [],
                salesDate: moment().format('YYYY-MM-DD HH:mm:ss'),
                items,
                payment: [createZeroAmountBookingPayment()],
                totalDiscount,
                paidAmount: 0,
                saleId: null,
            },
        };
    }

    async createMarkAsFreeSale({ setBookingDetails, setStatus, setLoading, settings }) {
        const isPOSActive = this?.settings?.profile?.outlet_addons?.some((addon) => addon.addon_name === 'POS');
        if (!isPOSActive || this?.bookingDetails?.booking?.sales_id) {
            return true;
        }

        try {
            const payload = this.buildMarkAsFreeSalesPayload();
            const response = await api?.postApiSale(payload);
            if (response?.data?.id) {
                await this?.getDetails({ setBookingDetails, setStatus, setLoading, settings });
                return true;
            }
            this?.toast.error(t('Calendar.MarkAsFreeSaleError'));
            return false;
        } catch (error) {
            console.error('Error creating mark-as-free sale:', error);
            this?.toast.error(t('Calendar.MarkAsFreeSaleError'));
            return false;
        }
    }

    // #Method 4 : Handle Payment
    async handlePayment({ addRefundItem, setCart, addItem, clearCart }) {
        const isPOSActive = this?.settings?.profile?.outlet_addons?.some((addon) => addon.addon_name === 'POS');
        if (!isPOSActive) {
            return;
        }

        // Clear existing cart so booking payment items are not mixed with previous POS items
        if (typeof clearCart === 'function') {
            clearCart();
        }
        clearCompletedPayments();

        const serviceTax = this.getServiceTax();

        const bookingServicesMap = this.getBookingServicesMap();
        const alreadyPaidAmount = this.getAlreadyPaidAmount();
        const projectedItems = [];
        bookingServicesMap.forEach((bookingService) => {
            projectedItems.push(
                this.buildServiceCartItem({
                    bookingService,
                    serviceTax,
                }),
            );
        });
        const projectedNetTotal = projectedItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
        const shouldUseZeroBookingPayment =
            projectedNetTotal === 0 && projectedItems.some((item) => item?.bookingId != null);

        if (this?.bookingDetails?.booking?.sales_id) {
            const processedServiceIds = new Set();

            this?.salesDetails?.items?.forEach((item) => {
                if (item?.itemType === 'OUTSTANDING') {
                    return;
                }

                if (item?.itemType === 'GIFT_CARD') {
                    const giftCardItem = {
                        salesDetailId: item?.id,
                        productId: item.productId,
                        serviceId: item.serviceId,
                        giftCardId: item.giftCardId,
                        itemName: item.itemName || '',
                        itemType: 'GIFT_CARD',
                        saleType: item.saleType != null ? item.saleType : 'SALE',
                        quantity: item.quantity ?? 1,
                        amount: Number(item.amount) ?? 0,
                        discountAmount: Number(item.discountAmount) ?? 0,
                        taxAmount: Number(item.taxAmount) ?? 0,
                        price: Number(item.price) ?? 0,
                        note: item.note || '',
                        employeeId: item.employeeId || Number(localStorage.getItem('employee_id')),
                        description: item.description || '',
                        discounts:
                            item.discounts?.map((discount) => ({
                                discountAmount: Number(discount.discountAmount) ?? 0,
                                amountType: discount.amountType || 'VARIABLE_PERCENTAGE',
                                amount: Number(discount.amount) ?? 0,
                            })) || [],
                        discountType: 'VARIABLE_PERCENTAGE',
                        tax: item.tax || [],
                    };

                    if (giftCardItem.amount < 0 || giftCardItem.saleType === 'RETURN') {
                        addRefundItem(giftCardItem);
                    } else {
                        addItem(giftCardItem);
                    }
                    return;
                }

                if (item?.itemType === 'SERVICE' && item?.serviceId != null) {
                    const bookingService = bookingServicesMap.get(Number(item.serviceId));
                    if (bookingService) {
                        processedServiceIds.add(Number(item.serviceId));
                        const newItem = this.buildServiceCartItem({
                            bookingService,
                            salesDetailId: item?.id ?? this.getSalesItemIdByServiceId(item.serviceId),
                            serviceTax,
                            existingSalesItem: item,
                        });

                        if (newItem.amount < 0 || newItem.saleType === 'RETURN') {
                            addRefundItem(newItem);
                        } else {
                            addItem(newItem);
                        }
                        return;
                    }
                }

                const itemAmount = Number(item.amount) ?? 0;
                const itemPrice = Number(item.price) ?? 0;
                const newItem = {
                    salesDetailId: item?.id,
                    productId: item.productId,
                    serviceId: item.serviceId,
                    itemName: item.itemName || '',
                    itemType: item.itemType != null ? item.itemType : 'PRODUCT',
                    saleType: item.saleType != null ? item.saleType : 'SALE',
                    quantity: item.quantity ?? 1,
                    amount: itemAmount,
                    discountAmount: Number(item.discountAmount) ?? 0,
                    taxAmount: Number(item.taxAmount) ?? 0,
                    price: itemPrice,
                    note: item.note || '',
                    employeeId: item.employeeId || Number(localStorage.getItem('employee_id')),
                    description: item.description || '',
                    discounts:
                        item.discounts?.map((discount) => ({
                            discountAmount: Number(discount.discountAmount) ?? 0,
                            amountType: discount.amountType || 'VARIABLE_PERCENTAGE',
                            amount: Number(discount.amount) ?? 0,
                        })) || [],
                    discountType: 'VARIABLE_PERCENTAGE',
                    tax: item.tax || (serviceTax && item.itemType === 'SERVICE' ? [serviceTax] : []),
                };

                if (newItem.amount < 0 || newItem.saleType === 'RETURN') {
                    addRefundItem(newItem);
                } else {
                    addItem(newItem);
                }
            });

            bookingServicesMap.forEach((bookingService, serviceId) => {
                if (processedServiceIds.has(serviceId)) {
                    return;
                }

                addItem(
                    this.buildServiceCartItem({
                        bookingService,
                        salesDetailId: this.getSalesItemIdByServiceId(serviceId),
                        serviceTax,
                    }),
                );
            });
        } else {
            bookingServicesMap.forEach((bookingService) => {
                addItem(
                    this.buildServiceCartItem({
                        bookingService,
                        serviceTax,
                    }),
                );
            });
        }
        setCart((prevCart) => ({
            ...prevCart,
            customerId: this?.bookingDetails?.booking?.outlet_customer?.id || null,
            customerName: this?.bookingDetails?.booking?.outlet_customer?.name || null,
            salesDate: moment().format('YYYY-MM-DD HH:mm:ss'),
            sellBy: Number(localStorage.getItem('employee_id')),
            salesType: 'SALES',
            paidAmount: this?.bookingDetails?.booking?.sales_id ? alreadyPaidAmount : 0,
            saleId: this?.bookingDetails?.booking?.sales_id || null,
            ...(shouldUseZeroBookingPayment ? { payment: [createZeroAmountBookingPayment()] } : {}),
        }));

        this?.closeForm();
        this?.navigate('/pos/sales?sellGiftCard=true');
    }

    // #method 5 : DELETE Booking
    async confirmDeleteBooking({ setShowDeleteConfirmation }) {
        const result = await this.updateBookingStatus({ status: 'DELETED' });
        if (result) {
            setShowDeleteConfirmation(false);
            this?.closeForm();
        }
    }

    // #method 6 : Cancel Booking
    async confirmCancelBooking({ setShowCancelConfirmation, admin_charge_cancellation = false }) {
        const result = await this.updateBookingStatus({ status: 'CANCELLED', admin_charge_cancellation });
        if (result) {
            setShowCancelConfirmation(false);
            this.closeForm();
        }
    }

    // # METHOD 7: Sell the service list as outstanding payment
    async handleSellAsOutstanding({ payload }) {
        try {
            const response = await api?.postApiSale(payload);
            if (response) {
                this.saleResponseId = response?.data?.id;
                this?.refetchSalesDetails(this?.saleResponseId || response?.data?.id);
            }
        } catch (error) {
            console.error('Error selling as outstanding payment:', error);
            // this?.toast.error('something went wrong');
        }
    }

    // #METHOD 8: Sell the service list as outstanding payment
    async noShowDetailsUpdate({ payload }) {
        try {
            await api?.patchApiBookingsNoShowId(this?.bookingId, payload);
        } catch (error) {
            console.error('Error selling as outstanding payment:', error);
            this?.toast.error('something went wrong');
        }
    }

    // #METHOD 9: Delete Group Booking
    async confirmDeleteGroupBooking({ setShowDeleteConfirmation, payload }) {
        const formPayload = { ...(payload ?? {}), google_calendar_sync: true };
        try {
            const result = await this.apiFetcher.post('/api/v1/store/booking/delete-bulk', formPayload);
            if (result) {
                this?.toast.success(t('Calendar.ToastSuccessDeleteBooking'));
                setShowDeleteConfirmation(false);
                this.closeForm();
            }
        } catch (error) {
            console.error('Error deleting group booking:', error);
            this?.toast.error(t('Calendar.ToastErrDeleteBooking'));
        }
    }

    // #METHOD 10: Bulk Cancel Bookings
    async bulkCancelBookings({ payload, setShowBulkConfirmation }) {
        const formPayload = { ...(payload ?? {}), google_calendar_sync: true };
        try {
            const response = await this.apiFetcher.post('api/v1/store/booking/cancel-bulk', formPayload);

            if (response.data.success) {
                this?.toast.success(response.data.msg || t('Calendar.ToastSuccessBulkCancel'));
                if (setShowBulkConfirmation) {
                    setShowBulkConfirmation(false);
                }
                this?.closeForm();
                return true;
            } else {
                this?.toast.error(response.data.msg || t('Calendar.ToastErrBulkCancel'));
                return false;
            }
        } catch (error) {
            console.error('Error bulk cancelling bookings:', error);
            this?.toast.error(t('Calendar.ToastErrBulkCancel'));
            return false;
        }
    }

    // #METHOD 11: Bulk Pending Bookings
    async bulkPendingBookings({ payload, setShowBulkConfirmation }) {
        const formPayload = { ...(payload ?? {}), google_calendar_sync: true };
        try {
            const response = await this.apiFetcher.post('api/v1/store/booking/pending-bulk', formPayload);

            if (response.data.success) {
                this?.toast.success(response.data.msg || t('Calendar.ToastSuccessBulkPending'));
                if (setShowBulkConfirmation) {
                    setShowBulkConfirmation(false);
                }
                this?.closeForm();
                return true;
            } else {
                this?.toast.error(response.data.msg || t('Calendar.ToastErrBulkPending'));
                return false;
            }
        } catch (error) {
            console.error('Error bulk pending bookings:', error);
            this?.toast.error(t('Calendar.ToastErrBulkPending'));
            return false;
        }
    }

    // #METHOD 12: Bulk Complete Bookings
    async bulkCompleteBookings({ payload, setShowBulkConfirmation }) {
        const formPayload = { ...(payload ?? {}), google_calendar_sync: true };
        try {
            const response = await this.apiFetcher.post('api/v1/store/booking/complete-bulk', formPayload);

            if (response.data.success) {
                this?.toast.success(response.data.msg || t('Calendar.ToastSuccessBulkComplete'));
                if (setShowBulkConfirmation) {
                    setShowBulkConfirmation(false);
                }
                this?.closeForm();
                return true;
            } else {
                this?.toast.error(response.data.msg || t('Calendar.ToastErrBulkComplete'));
                return false;
            }
        } catch (error) {
            console.error('Error bulk completing bookings:', error);
            this?.toast.error(t('Calendar.ToastErrBulkComplete'));
            return false;
        }
    }

    // #METHOD 13: Bulk No-Show Bookings
    async bulkNoShowBookings({ payload, setShowBulkConfirmation }) {
        const formPayload = { ...(payload ?? {}), google_calendar_sync: true };
        try {
            const response = await this.apiFetcher.post('api/v1/store/booking/noshow-bulk', formPayload);

            if (response.data.success) {
                this?.toast.success(response.data.msg || t('Calendar.ToastSuccessBulkNoShow'));
                if (setShowBulkConfirmation) {
                    setShowBulkConfirmation(false);
                }
                this?.closeForm();
                return true;
            } else {
                this?.toast.error(response.data.msg || t('Calendar.ToastErrBulkNoShow'));
                return false;
            }
        } catch (error) {
            console.error('Error bulk no-show bookings:', error);
            this?.toast.error(t('Calendar.ToastErrBulkNoShow'));
            return false;
        }
    }

    // #METHOD 14: UPDATE DYNAMIC API CONSTRUCTOR
    async updateConstructorValuesApi({ key, value }) {
        if (this[key]) {
            this[key] = value;
        }
    }

    // #METHOD 15: Sync Booking to Google Calendar
    async syncBookingToGoogleCalendar({ payload }) {
        try {
            const response = await this.apiFetcher.post('/api/google-calendar/booking-sync-by-group-uuid', payload);

            if (response.data.success) {
                this?.toast.success(response.data.msg || t('Calendar.ToastSuccessSyncToGoogleCalendar'));

                return true;
            } else {
                this?.toast.error(response.data.msg || t('Calendar.ToastErrSyncToGoogleCalendar'));
                return false;
            }
        } catch (error) {
            console.error('Error syncing booking to Google Calendar:', error);
            this?.toast.error(t('Calendar.ToastErrSyncToGoogleCalendar'));
            return false;
        }
    }

    // #METHOD 16: Sync Booking Status to Google Calendar
    async syncBookingStatusToGoogleCalendar({ payload }) {
        try {
            const response = await this.apiFetcher.get('/api/google-calendar/booking-status-by-group-uuid', {
                params: payload,
            });
            return response;
        } catch (error) {
            return error;
        }
    }

    // #METHOD 17: Delete Cancel Booking
    async handleDeleteCancelBooking(refreshBookings) {
        try {
            const response = await this.apiFetcher.delete(
                `/api/v1/store/service/cancellation_offer?booking_id=${this?.bookingDetails?.booking?.id}`,
            );
            if (response) {
                this?.toast.success(t('Calendar.CancellationDeleteMsg'));
                this?.closeForm();
                refreshBookings();
                return true;
            }
        } catch (error) {
            console.error('Error deleting cancel booking:', error);
            this?.toast.error(t('Calendar.ToastErrDeleteCancelBooking'));
            return false;
        }
    }

    // #METHOD 18: Mark booking as free (subscription inspection)
    async markAsFree({ setBookingDetails, setStatus, setLoading, settings, refreshBookings }) {
        try {
            const response = await MarkAsFreeApi({
                groupBookingUuid: this?.bookingDetails?.booking?.group_booking_uuid,
            });
            if (response?.data?.success) {
                this?.toast.success(response.data.msg || t('Calendar.MarkAsFreeSuccess'));
                await this?.getDetails({ setBookingDetails, setStatus, setLoading, settings });
                await this?.createMarkAsFreeSale({ setBookingDetails, setStatus, setLoading, settings });
                refreshBookings?.();
                return true;
            }
            this?.toast.error(response?.data?.msg || t('Calendar.MarkAsFreeError'));
            return false;
        } catch (error) {
            this?.toast.error(t('Calendar.MarkAsFreeError'));
            return false;
        }
    }

    // #METHOD 19: Set override inspection sales flag for group
    async setOverrideInspectionSales({ group_uuid, override_inspection_sales }) {
        try {
            if (!group_uuid) {
                this?.toast.error(t('Calendar.OverrideInspectionSalesError'));
                return false;
            }
            const response = await this.apiFetcher.post('api/v1/store/booking/override-inspection-sales', {
                group_uuid,
                override_inspection_sales: Boolean(override_inspection_sales),
            });
            if (response?.data?.success) {
                this?.toast.success(
                    override_inspection_sales
                        ? t('Calendar.OverrideInspectionSalesSuccess')
                        : t('Calendar.OverrideInspectionSalesCleared'),
                );
                return true;
            }
            this?.toast.error(t('Calendar.OverrideInspectionSalesError'));
            return false;
        } catch (error) {
            console.error('Error setting override inspection sales:', error);
            this?.toast.error(t('Calendar.OverrideInspectionSalesError'));
            return false;
        }
    }

    // #METHOD 19: promise for 3 api calls (sale as no show + no show details update + update booking status)
}
